import React, { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { signOut } from '../../services/auth';
import { CompactPresenceList } from '../Collaboration/PresenceList';
import PresenceList from '../Collaboration/PresenceList';

/**
 * Navbar component
 * MEMOIZED to prevent re-renders from parent on cursor movements
 * NOW RECEIVES onlineUsers as prop instead of calling usePresence()
 */
const Navbar = React.memo(function Navbar({ onShowShortcuts, onlineUsers = [] }) {
  console.log('🎨 Navbar RENDERING');
  
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPresenceList, setShowPresenceList] = useState(false);

  const handleSignOut = async () => {
    setLoading(true);
    try {
      await signOut();
      // Auth state change will automatically redirect to login
    } catch (error) {
      console.error('Error signing out:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get display name or fallback
  const displayName = currentUser?.displayName || currentUser?.email?.split('@')[0] || 'User';

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-xl border-b border-gray-200/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo/Title */}
          <div className="flex items-center">
            <div className="flex-shrink-0 flex items-center">
              {/* Logo Icon */}
              <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center mr-3">
                <svg 
                  className="w-5 h-5 text-white" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z" 
                  />
                </svg>
              </div>
              
              {/* App Title */}
              <h1 className="text-xl font-bold text-gray-900">
                CollabCanvas
              </h1>
              
              {/* Beta Badge (optional) */}
              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                Beta
              </span>
            </div>
          </div>

          {/* User Info & Actions */}
          <div className="flex items-center space-x-4">
            {/* Keyboard Shortcuts Button */}
            <button
              onClick={onShowShortcuts}
              className="inline-flex items-center justify-center w-9 h-9 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all duration-200 group"
              title="Keyboard shortcuts (?)"
            >
              <svg 
                className="w-5 h-5 group-hover:scale-110 transition-transform duration-200" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
                />
              </svg>
            </button>
            
            {/* Presence List */}
            <div className="relative">
              {/* Compact presence trigger */}
              <button
                onClick={() => setShowPresenceList(!showPresenceList)}
                className="hover:bg-gray-50 rounded-lg p-1 transition-colors duration-200"
              >
                <CompactPresenceList onlineUsers={onlineUsers} />
              </button>
              
              {/* Expandable presence list dropdown */}
              {showPresenceList && (
                <>
                  {/* Backdrop to close dropdown */}
                  <div 
                    className="fixed inset-0 z-10" 
                    onClick={() => setShowPresenceList(false)}
                  ></div>
                  
                  {/* Dropdown content */}
                  <div className="absolute right-0 mt-2 z-20">
                    <PresenceList onlineUsers={onlineUsers} />
                  </div>
                </>
              )}
            </div>

            {/* User Display Name */}
            <div className="flex items-center space-x-3">
              {/* User Avatar */}
              <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-full flex items-center justify-center">
                <span className="text-sm font-medium text-white">
                  {displayName.charAt(0).toUpperCase()}
                </span>
              </div>
              
              {/* User Name */}
              <div className="flex flex-col">
                <span className="text-sm font-medium text-gray-900">
                  {displayName}
                </span>
                <span className="text-xs text-gray-500 hidden sm:block">
                  {currentUser?.email}
                </span>
              </div>
            </div>

            {/* Logout Button */}
            <button 
              onClick={handleSignOut} 
              disabled={loading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-semibold rounded-xl text-white bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-105 shadow-md hover:shadow-lg"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Signing out...
                </div>
              ) : (
                <div className="flex items-center">
                  <svg 
                    className="w-4 h-4 mr-2" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" 
                    />
                  </svg>
                  Sign Out
                </div>
              )}
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
});

export default Navbar;
